import { useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useMediaQuery } from 'react-responsive';

import { SHORT_BRANDING_NAME } from 'constants/plusMembership';
import { IMPERFECT_THEME_DOMAIN, MISFITS_THEME_DOMAIN } from 'constants/theme';
import Button from 'honeydew/Button';
import SavingsGuaranteeCheckmarkIcon from 'honeydew/icons/PlusMembership/SavingsGuaranteeCheckmarkIcon';
import useGlobalStore from 'hooks/useGlobalStore';
import useTailwindTheme from 'hooks/useTailwindTheme';

import SavingsGuaranteeTermsModal from './SavingsGuaranteeTermsModal';

/**
 * Savings Guarantee perk banner with checkmark badge.
 * @param {'standard' | 'renewal' | 'memberDeals'} [variant='standard']
 * @param {() => void} [onMemberDealsCtaClick]
 * @returns {JSX.Element}
 */
function SavingsGuaranteePerk({ onMemberDealsCtaClick, variant = 'standard' }) {
    const isMemberDeals = variant === 'memberDeals';
    const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
    const { isMisfitsTheme } = useGlobalStore();
    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });

    const brandDomain = isMisfitsTheme ? MISFITS_THEME_DOMAIN : IMPERFECT_THEME_DOMAIN;
    const plusMembershipShortBranding = SHORT_BRANDING_NAME[brandDomain];

    const title = variant === 'renewal' ? 'Renewal Savings Guarantee' : 'Savings Guarantee';
    const bodyCopy =
        variant === 'renewal'
            ? `Your next year ${plusMembershipShortBranding} pays for itself, or we'll credit you the difference.`
            : `${plusMembershipShortBranding} pays for itself, or we'll credit you the difference.`;

    const renderInfoButton = () => (
        <button
            type="button"
            className="inline-flex shrink-0 items-center justify-center align-middle"
            aria-label="Savings guarantee terms"
            onClick={() => setIsTermsModalOpen(true)}
        >
            <InformationCircleIcon width="14px" aria-hidden />
        </button>
    );

    const renderBodyCopy = () => (
        <p className="text-body-sm flex items-center gap-5 text-white">
            <span>
                {bodyCopy}
                <span className="ml-1 inline-block">{renderInfoButton()}</span>
            </span>
        </p>
    );

    const renderMemberDealsCta = () => (
        <Button
            customClass="border-white text-cranberry hover:bg-white hover:text-cranberry"
            onClick={onMemberDealsCtaClick}
            rounded="full"
            size="sm"
            variant="secondary"
        >
            Start saving today
        </Button>
    );

    if (isMemberDeals) {
        return (
            <>
                <div className="relative overflow-hidden rounded-xl bg-cranberry p-10">
                    <div className="pl-60 md:pl-0">
                        <div className="md:flex md:items-center md:justify-center md:gap-10">
                            <div className="absolute top-1/2 left-10 -translate-y-1/2 md:static md:translate-y-0">
                                <SavingsGuaranteeCheckmarkIcon width={mobile ? 45 : 35} />
                            </div>
                            <div className="text-body-sm text-white">
                                <h5 className="mb-[2px] font-grotesk-bold md:mr-5 md:mb-0 md:inline">{title}</h5>
                                <p className="block md:inline">
                                    {bodyCopy}
                                    <span className="ml-1 inline-block align-middle">{renderInfoButton()}</span>
                                </p>
                            </div>
                        </div>
                        <div className="mt-5 md:flex md:justify-center">{renderMemberDealsCta()}</div>
                    </div>
                </div>
                <SavingsGuaranteeTermsModal open={isTermsModalOpen} onClose={() => setIsTermsModalOpen(false)} />
            </>
        );
    }

    return (
        <>
            <div className="relative overflow-hidden rounded-xl bg-cranberry p-10">
                <div className="pr-70">
                    <h5 className="text-body-sm mb-[2px] font-grotesk-bold text-white">{title}</h5>
                    {renderBodyCopy()}
                </div>
                <div className="absolute top-1/2 right-10 -translate-y-1/2">
                    <SavingsGuaranteeCheckmarkIcon width={45} />
                </div>
            </div>
            <SavingsGuaranteeTermsModal open={isTermsModalOpen} onClose={() => setIsTermsModalOpen(false)} />
        </>
    );
}

export default SavingsGuaranteePerk;
